import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import {
  Wallet, HandCoins, Target, ShieldAlert, Plus, Search, Filter,
  Download, Receipt, Trash2, X, FileText, Image as ImageIcon, AlertTriangle, Pencil,
  FolderKanban, Building2, Users as UsersIcon, Package,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPES = [
  { id: 'donation',        labelKey: 'finance.types.donation',       icon: HandCoins,   color: 'text-emerald-600', bg: 'bg-emerald-50',  border: 'border-emerald-200' },
  { id: 'project_expense', labelKey: 'finance.types.projectExpense', icon: Target,      color: 'text-sky-600',     bg: 'bg-sky-50',      border: 'border-sky-200' },
  { id: 'admin_expense',   labelKey: 'finance.types.adminExpense',   icon: ShieldAlert, color: 'text-orange-600',  bg: 'bg-orange-50',   border: 'border-orange-200' },
];

const formatEuro = (n) => `€ ${Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ============================================================================
// Projects finance panel — per-project budget/spent/goal cards
// ============================================================================
const ProjectsFinancePanel = ({ projects, rollups, onOpen, t }) => {
  if (!projects.length) {
    return (
      <div className="text-center text-zinc-400 py-16 text-sm border-2 border-dashed border-zinc-200 rounded-2xl">
        <FolderKanban className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
        {t('finance.emptyProjects')}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {projects.map((p) => {
        const r = rollups[p.id];
        const hasBudget = r?.budget_amount != null;
        const hasGoal = r?.goal_amount != null;
        const budgetPct = Math.max(0, Math.min(100, r?.budget_used_pct ?? 0));
        const goalPct = Math.max(0, Math.min(100, r?.goal_progress_pct ?? 0));
        const overBudget = hasBudget && r.spent > r.budget_amount;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onOpen(p)}
            data-testid={`finance-project-card-${p.id}`}
            className="text-left bg-white border border-zinc-200 hover:border-zinc-300 rounded-2xl p-4 transition-all hover:shadow-sm"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-zinc-900 truncate">{p.title}</h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  {p.start_date} – {p.end_date} · <span className="capitalize">{p.computed_status}</span>
                </p>
              </div>
              {r && (
                <div className={`text-right text-xs ${overBudget ? 'text-red-600' : 'text-zinc-500'}`}>
                  {r.entry_count} {t('finance.entries')}
                </div>
              )}
            </div>

            {r && (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-emerald-50 rounded-lg py-1.5">
                    <div className="text-[9px] uppercase font-bold text-emerald-700 tracking-wide">{t('projects.donated')}</div>
                    <div className="text-xs font-bold text-emerald-900 tabular-nums">{formatEuro(r.donated)}</div>
                  </div>
                  <div className="bg-sky-50 rounded-lg py-1.5">
                    <div className="text-[9px] uppercase font-bold text-sky-700 tracking-wide">{t('projects.spent')}</div>
                    <div className="text-xs font-bold text-sky-900 tabular-nums">{formatEuro(r.spent)}</div>
                  </div>
                  <div className={`${overBudget ? 'bg-red-50' : 'bg-zinc-50'} rounded-lg py-1.5`}>
                    <div className={`text-[9px] uppercase font-bold tracking-wide ${overBudget ? 'text-red-700' : 'text-zinc-600'}`}>
                      {t('projects.remaining')}
                    </div>
                    <div className={`text-xs font-bold tabular-nums ${overBudget ? 'text-red-800' : 'text-zinc-900'}`}>
                      {hasBudget ? formatEuro(r.budget_remaining) : '—'}
                    </div>
                  </div>
                </div>

                {hasBudget && (
                  <div>
                    <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-0.5">
                      <span>{t('projects.budgetUsed')}</span>
                      <span className={`font-semibold tabular-nums ${overBudget ? 'text-red-600' : 'text-zinc-700'}`}>{budgetPct}%</span>
                    </div>
                    <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className={`h-full ${overBudget ? 'bg-red-500' : 'bg-sky-500'} transition-all duration-500`} style={{ width: `${Math.min(100, budgetPct)}%` }} />
                    </div>
                  </div>
                )}

                {hasGoal && (
                  <div>
                    <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-0.5">
                      <span>{t('projects.goalProgress')}</span>
                      <span className="font-semibold text-zinc-700 tabular-nums">{goalPct}%</span>
                    </div>
                    <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${goalPct}%` }} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ============================================================================
// Fixed monthly costs panel — office supplies / salaries / fixed
// ============================================================================
const CATEGORY_META = {
  office_supplies: { icon: Package,   labelKey: 'finance.fixed.office_supplies', tone: 'text-sky-700 bg-sky-50 border-sky-200' },
  salary:          { icon: UsersIcon, labelKey: 'finance.fixed.salary',          tone: 'text-violet-700 bg-violet-50 border-violet-200' },
  fixed:           { icon: Building2, labelKey: 'finance.fixed.fixed',           tone: 'text-amber-700 bg-amber-50 border-amber-200' },
};

const FixedCostsPanel = ({ items, summary, onChanged, getAuthHeader, t }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (i) => { setEditing(i); setDialogOpen(true); };

  const save = async (payload) => {
    try {
      if (editing) {
        await axios.put(`${API_URL}/finance/fixed-costs/${editing.id}`, payload, { headers: getAuthHeader() });
        toast.success(t('finance.fixed.updated'));
      } else {
        await axios.post(`${API_URL}/finance/fixed-costs`, payload, { headers: getAuthHeader() });
        toast.success(t('finance.fixed.created'));
      }
      setDialogOpen(false); setEditing(null);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('finance.error'));
    }
  };

  const remove = async (i) => {
    if (!window.confirm(t('finance.fixed.confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/finance/fixed-costs/${i.id}`, { headers: getAuthHeader() });
      toast.success(t('finance.fixed.deleted'));
      onChanged();
    } catch (_) { toast.error(t('finance.error')); }
  };

  const byCat = { office_supplies: [], salary: [], fixed: [] };
  items.forEach(i => { (byCat[i.category] || byCat.fixed).push(i); });

  return (
    <div className="space-y-5">
      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['office_supplies', 'salary', 'fixed'].map(cat => {
            const meta = CATEGORY_META[cat];
            const Icon = meta.icon;
            const val = summary.by_category?.[cat]?.total || 0;
            const count = summary.by_category?.[cat]?.count || 0;
            return (
              <div key={cat} className={`rounded-xl border p-3 ${meta.tone}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-bold">
                  <Icon className="w-3 h-3" />
                  {t(meta.labelKey)}
                </div>
                <div className="text-lg font-bold mt-1 tabular-nums">{formatEuro(val)}</div>
                <div className="text-[10px] opacity-70 mt-0.5">{count} {t('finance.fixed.entries')}</div>
              </div>
            );
          })}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 text-white p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-bold opacity-80">
              <Wallet className="w-3 h-3" />
              {t('finance.fixed.monthlyTotal')}
            </div>
            <div className="text-lg font-bold mt-1 tabular-nums">{formatEuro(summary.total_monthly)}</div>
            <div className="text-[10px] opacity-70 mt-0.5">{t('finance.fixed.perMonth')}</div>
          </div>
        </div>
      )}

      {/* Add button */}
      <Button onClick={openCreate} className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full h-10" data-testid="fixed-cost-add-btn">
        <Plus className="w-4 h-4 mr-1.5" />
        {t('finance.fixed.addNew')}
      </Button>

      {/* Items grouped by category */}
      {['office_supplies', 'salary', 'fixed'].map(cat => {
        const list = byCat[cat] || [];
        if (list.length === 0) return null;
        const meta = CATEGORY_META[cat];
        const Icon = meta.icon;
        return (
          <section key={cat}>
            <div className="flex items-center gap-2 mb-2 text-sm font-bold uppercase text-zinc-700 tracking-wider">
              <Icon className="w-4 h-4" />
              {t(meta.labelKey)}
              <span className="text-xs text-zinc-400 font-normal">({list.length})</span>
            </div>
            <div className="space-y-2">
              {list.map(i => (
                <div key={i.id} className="bg-white border border-zinc-200 rounded-xl p-3 flex items-center gap-3" data-testid={`fixed-cost-${i.id}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${meta.tone.split(' ').filter(x => x.startsWith('bg-')).join(' ')}`}>
                    <Icon className={`w-4 h-4 ${meta.tone.split(' ').filter(x => x.startsWith('text-')).join(' ')}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-900 truncate">{i.name}</div>
                    {i.note && <div className="text-xs text-zinc-500 truncate">{i.note}</div>}
                  </div>
                  <div className="font-bold text-zinc-900 tabular-nums">{formatEuro(i.amount)}</div>
                  <button type="button" onClick={() => openEdit(i)} className="p-1.5 rounded-lg text-zinc-500 hover:bg-zinc-100" data-testid={`fixed-cost-edit-${i.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" data-testid={`fixed-cost-delete-${i.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {items.length === 0 && (
        <div className="text-center text-zinc-400 py-12 text-sm border-2 border-dashed border-zinc-200 rounded-2xl">
          <Building2 className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
          {t('finance.fixed.empty')}
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditing(null); } }}>
        <DialogContent className="sm:max-w-md" aria-describedby="fixed-cost-form-desc">
          <DialogHeader>
            <DialogTitle>{editing ? t('finance.fixed.edit') : t('finance.fixed.addNew')}</DialogTitle>
            <p id="fixed-cost-form-desc" className="sr-only">{t('finance.fixed.addNew')}</p>
          </DialogHeader>
          <FixedCostForm
            initial={editing}
            onSubmit={save}
            onCancel={() => { setDialogOpen(false); setEditing(null); }}
            t={t}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FixedCostForm = ({ initial, onSubmit, onCancel, t }) => {
  const [category, setCategory] = useState(initial?.category || 'office_supplies');
  const [name, setName] = useState(initial?.name || '');
  const [amount, setAmount] = useState(initial?.amount == null ? '' : String(initial.amount));
  const [note, setNote] = useState(initial?.note || '');

  const submit = () => {
    if (!name.trim()) { toast.error(t('finance.fixed.nameRequired')); return; }
    const amt = parseFloat(String(amount).replace(',', '.'));
    if (isNaN(amt) || amt < 0) { toast.error(t('finance.fixed.amountInvalid')); return; }
    onSubmit({
      category,
      name: name.trim(),
      amount: amt,
      note: note.trim() || null,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium">{t('finance.fixed.category')}</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="mt-1.5 h-11" data-testid="fixed-cost-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="office_supplies">{t('finance.fixed.office_supplies')}</SelectItem>
            <SelectItem value="salary">{t('finance.fixed.salary')}</SelectItem>
            <SelectItem value="fixed">{t('finance.fixed.fixed')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-sm font-medium">{t('finance.fixed.name')}</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('finance.fixed.namePh')} className="mt-1.5 h-11" data-testid="fixed-cost-name" autoFocus />
      </div>
      <div>
        <Label className="text-sm font-medium">{t('finance.fixed.amount')}</Label>
        <div className="relative mt-1.5">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">€</span>
          <Input type="number" inputMode="decimal" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" className="pl-7 h-11" data-testid="fixed-cost-amount" />
        </div>
      </div>
      <div>
        <Label className="text-sm font-medium">{t('finance.note')} <span className="text-zinc-400 font-normal">({t('finance.optional')})</span></Label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1.5 resize-none text-sm" />
      </div>
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={onCancel} className="rounded-full">{t('common.cancel')}</Button>
        <Button onClick={submit} className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full" data-testid="fixed-cost-save">{t('common.save')}</Button>
      </DialogFooter>
    </div>
  );
};

// Resize an image File to a JPEG data URI under ~1.5 MB
const fileToDataURI = (file, maxDim = 1200, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    if (file.type === 'application/pdf') {
      resolve(reader.result); // PDFs are passed through as-is
      return;
    }
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale); height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      try { resolve(canvas.toDataURL('image/jpeg', quality)); } catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const EntryForm = ({ initial, onSubmit, onCancel, submitting, projects = [] }) => {
  const { t } = useTranslation();
  const [type, setType] = useState(initial?.type || 'donation');
  const [amount, setAmount] = useState(initial?.amount?.toString() || '');
  const [date, setDate] = useState(initial?.date || format(new Date(), 'yyyy-MM-dd'));
  const [title, setTitle] = useState(initial?.title || '');
  const [note, setNote] = useState(initial?.note || '');
  const [donorName, setDonorName] = useState(initial?.donor_name || '');
  const [projectId, setProjectId] = useState(initial?.project_id || '__none__');
  const [receipt, setReceipt] = useState(initial?.receipt_url || null);
  const [receiptName, setReceiptName] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error(t('finance.receiptInvalid'));
      return;
    }
    try {
      const uri = await fileToDataURI(file);
      if (uri.length > 2_500_000) {
        toast.error(t('finance.receiptTooLarge'));
        return;
      }
      setReceipt(uri);
      setReceiptName(file.name);
    } catch (_) { toast.error(t('finance.receiptError')); }
  };

  const handleSubmit = () => {
    const amt = parseFloat(String(amount).replace(',', '.'));
    if (!amt || amt <= 0) { toast.error(t('finance.amountInvalid')); return; }
    if (!title.trim()) { toast.error(t('finance.titleRequired')); return; }
    onSubmit({
      type, amount: amt, date, title: title.trim(), note: note.trim() || null,
      donor_name: type === 'donation' ? (donorName.trim() || null) : null,
      receipt_url: receipt,
      project_id: projectId === '__none__' ? null : projectId,
    });
  };

  return (
    <div className="space-y-5">
      {/* Type */}
      <div>
        <Label className="text-sm font-semibold mb-2 block">{t('finance.type')}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {TYPES.map(opt => {
            const Icon = opt.icon;
            const active = type === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setType(opt.id)}
                data-testid={`finance-type-${opt.id}`}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  active ? `${opt.bg} ${opt.border} ${opt.color} shadow-sm` : 'border-zinc-200 hover:border-zinc-300 bg-white'
                }`}
              >
                <Icon className="w-5 h-5 mb-1" />
                <div className="text-sm font-medium">{t(opt.labelKey)}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-medium">{t('finance.amount')}</Label>
          <Input
            type="number" step="0.01" min="0"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0,00"
            className="mt-1.5 h-11 text-base"
            data-testid="finance-amount-input"
            autoFocus
          />
        </div>
        <div>
          <Label className="text-sm font-medium">{t('finance.date')}</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1.5 h-11"
            data-testid="finance-date-input"
          />
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium">{t('finance.titleLabel')}</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('finance.titlePlaceholder')}
          className="mt-1.5 h-11"
          data-testid="finance-title-input"
        />
      </div>

      {type === 'donation' && (
        <div>
          <Label className="text-sm font-medium">{t('finance.donor')} <span className="text-zinc-400 font-normal">({t('finance.optional')})</span></Label>
          <Input
            value={donorName}
            onChange={(e) => setDonorName(e.target.value)}
            placeholder={t('finance.donorPlaceholder')}
            className="mt-1.5 h-11"
            data-testid="finance-donor-input"
          />
        </div>
      )}

      <div>
        <Label className="text-sm font-medium">{t('finance.project')} <span className="text-zinc-400 font-normal">({t('finance.optional')})</span></Label>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="mt-1.5 h-11" data-testid="finance-project-select">
            <SelectValue placeholder={t('finance.projectPh')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{t('finance.noProject')}</SelectItem>
            {projects.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-zinc-500 mt-1">{t('finance.projectHint')}</p>
      </div>

      <div>
        <Label className="text-sm font-medium">{t('finance.note')} <span className="text-zinc-400 font-normal">({t('finance.optional')})</span></Label>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('finance.notePlaceholder')}
          className="mt-1.5 resize-none"
          rows={2}
          data-testid="finance-note-input"
        />
      </div>

      {/* Receipt */}
      <div>
        <Label className="text-sm font-medium">{t('finance.receipt')} <span className="text-zinc-400 font-normal">({t('finance.optional')})</span></Label>
        {receipt ? (
          <div className="mt-1.5 p-3 bg-zinc-50 rounded-xl border border-zinc-200 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {receipt.startsWith('data:application/pdf') ? (
                <FileText className="w-5 h-5 text-zinc-600 flex-shrink-0" />
              ) : (
                <ImageIcon className="w-5 h-5 text-zinc-600 flex-shrink-0" />
              )}
              <span className="text-sm text-zinc-700 truncate">{receiptName || t('finance.receiptAttached')}</span>
            </div>
            <button onClick={() => { setReceipt(null); setReceiptName(''); }} className="text-zinc-500 hover:text-red-600 p-1" type="button" data-testid="finance-receipt-remove">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label
            className="mt-1.5 flex items-center justify-center gap-2 p-4 border-2 border-dashed border-zinc-300 rounded-xl text-zinc-500 hover:border-zinc-400 cursor-pointer transition-colors"
            data-testid="finance-receipt-upload"
          >
            <Receipt className="w-5 h-5" />
            <span className="text-sm font-medium">{t('finance.uploadReceipt')}</span>
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} capture="environment" />
          </label>
        )}
      </div>

      <DialogFooter className="gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="rounded-full px-6">{t('common.cancel')}</Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="finance-submit-btn"
          className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6"
        >
          {submitting ? t('common.loading') : t('common.save')}
        </Button>
      </DialogFooter>
    </div>
  );
};

const FinancePage = () => {
  const { user, getAuthHeader } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectRollups, setProjectRollups] = useState({});
  const [fixedCosts, setFixedCosts] = useState([]);
  const [fixedCostsSummary, setFixedCostsSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewEntry, setViewEntry] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('entries');
  const getLocale = () => i18n.language === 'de' ? de : enUS;

  const fetchSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/finance/summary`, { headers: getAuthHeader() });
      setSummary(r.data);
    } catch (error) {
      console.error('Failed to fetch finance summary:', error);
    }
  }, [getAuthHeader]);

  const fetchEntries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (projectFilter !== 'all') params.set('project_id', projectFilter);
      if (search.trim()) params.set('search', search.trim());
      params.set('limit', '200');
      const r = await axios.get(`${API_URL}/finance/entries?${params.toString()}`, { headers: getAuthHeader() });
      setEntries(r.data || []);
    } catch (error) {
      console.error('Failed to fetch finance entries:', error);
    }
  }, [getAuthHeader, typeFilter, projectFilter, search]);

  const fetchProjects = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/projects`, { headers: getAuthHeader() });
      setProjects(r.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, [getAuthHeader]);

  const fetchProjectRollups = useCallback(async (list) => {
    const results = await Promise.all(
      list.map(async (p) => {
        try {
          const r = await axios.get(`${API_URL}/projects/${p.id}/finance`, { headers: getAuthHeader() });
          return [p.id, r.data];
        } catch (error) {
          console.error(`Failed to fetch finance rollup for project ${p.id}:`, error);
          return [p.id, null];
        }
      })
    );
    setProjectRollups(Object.fromEntries(results.filter(([, v]) => v)));
  }, [getAuthHeader]);

  const fetchFixedCosts = useCallback(async () => {
    try {
      const [listR, sumR] = await Promise.all([
        axios.get(`${API_URL}/finance/fixed-costs`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/finance/fixed-costs/summary`, { headers: getAuthHeader() }),
      ]);
      setFixedCosts(listR.data || []);
      setFixedCostsSummary(sumR.data);
    } catch (error) {
      console.error('Failed to fetch fixed costs:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/home', { replace: true });
      return;
    }
    fetchSummary();
    fetchEntries();
    fetchProjects();
    fetchFixedCosts();
  }, [user?.role, navigate, fetchSummary, fetchEntries, fetchProjects, fetchFixedCosts]);

  useEffect(() => {
    if (projects.length > 0) fetchProjectRollups(projects);
  }, [projects, fetchProjectRollups]);

  const loading = summary === null;

  const handleSave = async (payload) => {
    setSubmitting(true);
    try {
      if (editingEntry) {
        await axios.put(`${API_URL}/finance/entries/${editingEntry.id}`, payload, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API_URL}/finance/entries`, payload, { headers: getAuthHeader() });
      }
      toast.success(t('finance.saved'));
      setDialogOpen(false);
      setEditingEntry(null);
      await Promise.all([fetchSummary(), fetchEntries()]);
      fetchProjectRollups(projects);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('finance.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(t('finance.confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/finance/entries/${entry.id}`, { headers: getAuthHeader() });
      toast.success(t('finance.deleted'));
      setViewEntry(null);
      await Promise.all([fetchSummary(), fetchEntries()]);
      fetchProjectRollups(projects);
    } catch (_) { toast.error(t('finance.error')); }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.set('type', typeFilter);
      const r = await axios.get(`${API_URL}/finance/export?${params.toString()}`, {
        headers: getAuthHeader(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `asunya_finanzen_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error(t('finance.error')); }
  };

  const openViewEntry = async (entry) => {
    try {
      // Fetch including receipt
      const r = await axios.get(`${API_URL}/finance/entries/${entry.id}`, { headers: getAuthHeader() });
      setViewEntry(r.data);
    } catch (_) { setViewEntry(entry); }
  };

  const filteredEntries = entries;
  const adminWarning = summary && summary.admin_used_pct > 80 && summary.admin_budget_total > 0;

  const totals = useMemo(() => ({
    donations: summary?.total_donations || 0,
    projectLeft: summary?.project_budget_left || 0,
    adminLeft: summary?.admin_budget_left || 0,
    adminPct: summary?.admin_used_pct || 0,
    adminTotal: summary?.admin_budget_total || 0,
    adminSpent: summary?.total_admin_expenses || 0,
  }), [summary]);

  if (user?.role !== 'admin') return null;

  return (
    <Layout>
      <div data-testid="finance-page" className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('finance.title')}
            </h1>
            <p className="text-zinc-600 text-sm mt-1">{t('finance.subtitle')}</p>
          </div>
          <Button
            onClick={handleExport}
            variant="outline"
            className="rounded-full text-sm h-10"
            data-testid="finance-export-btn"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {t('finance.exportCsv')}
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="finance-summary">
          <SummaryCard
            tone="emerald"
            icon={HandCoins}
            label={t('finance.cards.donations')}
            value={formatEuro(totals.donations)}
            sub={summary?.counts?.donation ? `${summary.counts.donation} ${t('finance.entries')}` : t('finance.noEntries')}
            testId="card-donations"
          />
          <SummaryCard
            tone="sky"
            icon={Target}
            label={t('finance.cards.projectBudget')}
            value={formatEuro(totals.projectLeft)}
            sub={`${t('finance.cards.of')} ${formatEuro(summary?.project_budget_total || 0)} (90 %)`}
            testId="card-project"
          />
          <SummaryCard
            tone="orange"
            icon={ShieldAlert}
            label={t('finance.cards.adminLeft')}
            value={formatEuro(totals.adminLeft)}
            sub={`${formatEuro(totals.adminSpent)} ${t('finance.cards.of')} ${formatEuro(totals.adminTotal)} (10 %)`}
            progress={totals.adminPct}
            warning={adminWarning}
            testId="card-admin"
          />
        </div>

        {adminWarning && (
          <div className="bg-orange-50 border border-orange-200 text-orange-800 rounded-xl p-3 flex items-center gap-2 text-sm" data-testid="admin-budget-warning">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {t('finance.adminWarning', { pct: totals.adminPct })}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-11 rounded-full bg-zinc-100 p-1">
            <TabsTrigger value="entries" className="rounded-full text-sm" data-testid="finance-tab-entries">
              {t('finance.tabs.entries')}
            </TabsTrigger>
            <TabsTrigger value="projects" className="rounded-full text-sm" data-testid="finance-tab-projects">
              <FolderKanban className="w-3.5 h-3.5 mr-1.5" />
              {t('finance.tabs.projects')}
            </TabsTrigger>
            <TabsTrigger value="fixed" className="rounded-full text-sm" data-testid="finance-tab-fixed">
              <Building2 className="w-3.5 h-3.5 mr-1.5" />
              {t('finance.tabs.fixed')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="entries" className="mt-5 space-y-4">
        {/* Filters */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('finance.searchPlaceholder')}
              className="border-0 shadow-none focus-visible:ring-0 h-9 px-0"
              data-testid="finance-search"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <FilterPill active={typeFilter === 'all'} onClick={() => setTypeFilter('all')} testId="filter-all">
              <Filter className="w-3.5 h-3.5 mr-1" />
              {t('finance.filters.all')}
            </FilterPill>
            {TYPES.map(opt => (
              <FilterPill key={opt.id} active={typeFilter === opt.id} onClick={() => setTypeFilter(opt.id)} testId={`filter-${opt.id}`}>
                {t(opt.labelKey)}
              </FilterPill>
            ))}
            {projects.length > 0 && (
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="h-8 w-auto min-w-[140px] rounded-full border-zinc-200 text-xs" data-testid="finance-project-filter">
                  <SelectValue placeholder={t('finance.filterByProject')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('finance.allProjects')}</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Entry list */}
        <div className="space-y-2">
          {loading ? (
            <div className="text-center text-zinc-400 py-12 text-sm">{t('common.loading')}</div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center text-zinc-400 py-12 text-sm">
              <Wallet className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
              {t('finance.emptyList')}
            </div>
          ) : filteredEntries.map((e, idx) => {
            const typeMeta = TYPES.find(x => x.id === e.type) || TYPES[0];
            const Icon = typeMeta.icon;
            const isExpense = e.type !== 'donation';
            return (
              <motion.button
                type="button"
                key={e.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx, 10) * 0.02 }}
                onClick={() => openViewEntry(e)}
                data-testid={`finance-row-${e.id}`}
                className={`w-full text-left p-3 rounded-xl border ${typeMeta.border} ${typeMeta.bg} hover:shadow-sm transition-all flex items-center gap-3`}
              >
                <div className={`w-9 h-9 rounded-full ${typeMeta.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 ${typeMeta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-zinc-900 truncate">{e.title}</span>
                    {e.donor_name && (
                      <span className="text-xs text-zinc-500">· {e.donor_name}</span>
                    )}
                    {e.project_id && (() => {
                      const pr = projects.find(p => p.id === e.project_id);
                      return pr ? (
                        <span className="text-[10px] uppercase font-bold text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded-full">
                          {pr.title}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {format(parseISO(e.date), 'd. MMM yyyy', { locale: getLocale() })}
                  </div>
                </div>
                <div className={`font-bold text-base ${isExpense ? 'text-zinc-700' : 'text-emerald-700'}`}>
                  {isExpense ? '−' : '+'}{formatEuro(e.amount)}
                </div>
              </motion.button>
            );
          })}
        </div>
          </TabsContent>

          <TabsContent value="projects" className="mt-5">
            <ProjectsFinancePanel
              projects={projects}
              rollups={projectRollups}
              onOpen={(p) => navigate(`/projects/${p.id}`)}
              t={t}
            />
          </TabsContent>

          <TabsContent value="fixed" className="mt-5">
            <FixedCostsPanel
              items={fixedCosts}
              summary={fixedCostsSummary}
              onChanged={fetchFixedCosts}
              getAuthHeader={getAuthHeader}
              t={t}
            />
          </TabsContent>
        </Tabs>

        {/* Floating + button */}
        <button
          type="button"
          onClick={() => { setEditingEntry(null); setDialogOpen(true); }}
          data-testid="finance-add-btn"
          className="fixed bottom-24 right-4 sm:bottom-8 sm:right-8 z-30 w-14 h-14 rounded-full bg-[#D93025] hover:bg-[#B91C1C] text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingEntry(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="finance-form-desc">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? t('finance.editEntry') : t('finance.newEntry')}
            </DialogTitle>
            <p id="finance-form-desc" className="sr-only">{t('finance.title')}</p>
          </DialogHeader>
          <EntryForm
            key={editingEntry?.id || 'new'}
            initial={editingEntry}
            submitting={submitting}
            projects={projects}
            onCancel={() => { setDialogOpen(false); setEditingEntry(null); }}
            onSubmit={handleSave}
          />
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewEntry} onOpenChange={(o) => { if (!o) setViewEntry(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="finance-view-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{viewEntry?.title}</DialogTitle>
            <p id="finance-view-desc" className="sr-only">{t('finance.title')}</p>
          </DialogHeader>
          {viewEntry && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Detail label={t('finance.amount')} value={formatEuro(viewEntry.amount)} />
                <Detail label={t('finance.date')} value={viewEntry.date ? format(parseISO(viewEntry.date), 'd. MMM yyyy', { locale: getLocale() }) : ''} />
                <Detail label={t('finance.type')} value={t(TYPES.find(x => x.id === viewEntry.type)?.labelKey || '')} />
                {viewEntry.donor_name && <Detail label={t('finance.donor')} value={viewEntry.donor_name} />}
              </div>
              {viewEntry.note && (
                <div className="bg-zinc-50 rounded-xl p-3">
                  <div className="text-xs text-zinc-500 mb-1">{t('finance.note')}</div>
                  <div className="text-sm text-zinc-800 whitespace-pre-wrap">{viewEntry.note}</div>
                </div>
              )}
              {viewEntry.receipt_url && (
                <div>
                  <div className="text-xs text-zinc-500 mb-1">{t('finance.receipt')}</div>
                  {viewEntry.receipt_url.startsWith('data:application/pdf') ? (
                    <a href={viewEntry.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-[#D93025] hover:underline">
                      <FileText className="w-4 h-4" />
                      {t('finance.openPdf')}
                    </a>
                  ) : (
                    <img src={viewEntry.receipt_url} alt="Beleg" className="rounded-xl border border-zinc-200 max-h-72 w-auto" />
                  )}
                </div>
              )}
              <div className="text-xs text-zinc-400">
                {t('finance.createdBy')}: {viewEntry.created_by_name || '—'}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setEditingEntry(viewEntry); setViewEntry(null); setDialogOpen(true); }}
              className="rounded-full"
              data-testid="finance-edit-btn"
            >
              <Pencil className="w-4 h-4 mr-1.5" /> {t('common.edit')}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDelete(viewEntry)}
              className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
              data-testid="finance-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

const SummaryCard = ({ icon: Icon, label, value, sub, progress, warning, tone, testId }) => {
  const toneMap = {
    emerald: { bg: 'bg-emerald-50',  text: 'text-emerald-700', icon: 'text-emerald-600',  bar: 'bg-emerald-500' },
    sky:     { bg: 'bg-sky-50',      text: 'text-sky-700',     icon: 'text-sky-600',      bar: 'bg-sky-500' },
    orange:  { bg: 'bg-orange-50',   text: 'text-orange-700',  icon: 'text-orange-600',   bar: 'bg-orange-500' },
  }[tone] || { bg: 'bg-zinc-50', text: 'text-zinc-700', icon: 'text-zinc-600', bar: 'bg-zinc-500' };
  return (
    <div data-testid={testId} className={`rounded-2xl p-4 border border-zinc-200 ${toneMap.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-5 h-5 ${toneMap.icon}`} />
        {warning && <AlertTriangle className="w-4 h-4 text-orange-600" />}
      </div>
      <div className="text-xs text-zinc-500 font-medium uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${toneMap.text}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
      {typeof progress === 'number' && (
        <div className="mt-3 h-1.5 bg-white rounded-full overflow-hidden">
          <div
            className={`h-full ${warning ? 'bg-orange-500' : toneMap.bar} transition-all`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
};

const FilterPill = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors inline-flex items-center ${
      active ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
    }`}
  >
    {children}
  </button>
);

const Detail = ({ label, value }) => (
  <div className="bg-zinc-50 rounded-xl p-3">
    <div className="text-xs text-zinc-500 mb-0.5">{label}</div>
    <div className="text-sm font-semibold text-zinc-900">{value}</div>
  </div>
);

export default FinancePage;
